/**
 * The live stamp preview MODEL (SPEC_STAMP_ROUND_20260924 §C.4; DEC-095 C1; contract v0.1.12 §2) — pure.
 *
 * `previewModel(fix, permission, stamp, now, boundFix)` turns the exchange's `summary.stamp`, the fix the
 * next chunk header is built from (or the fix a shot in flight FROZE), and the clock into what the card
 * shows. Two rules outrank everything else (§C.1):
 *   1. never a value the server will not bind — the fix is `shotGeo.latestFix()` / `handle.boundFix`;
 *      the coordinates follow `liveCard` (DEC-095: the device's OWN reading, every kind), never the
 *      copy election `coordinates`, which only adds the honest "not printed on shared copies" note;
 *   2. a view only — nothing here adds or alters evidence.
 *
 * Client-side geo mirrors the be exactly and the be's dumps are the oracles:
 *   • `haversineMetres` / `bandOf` = `GeoMath.haversineMetres` / `GeoMath.band` (R = 6 371 008.8 m;
 *     B50 ≤ 50 · B200 ≤ 200 · B1000 ≤ 1000 · OVER) — `sv-geo-band-vectors.json`;
 *   • `metresWords` = `SvStampComposer.metres` (`42 m` below 999.5, else `1.2 km`);
 *   • the band WORDS live in the deck (`sv.capture.stamp.band.*`) and are pinned to
 *     `SvStampComposer.bandWordsShort` — `within 50 m` from `sv-stamp-lines.json`, the rest from the source;
 *   • the Plus code = `plusCode.ts`, oracle `sv-stamp-lines.json` `show.lines[2]`;
 *   • `localClock` = line 2's `14:40 IST` form, in the TENANT zone the exchange names.
 * A centroid anchor (`confidence LOW`) never yields metres — "not established (pincode centroid only)";
 * no anchor ⇒ "measured by the server after capture". Never a street the browser derived: the only
 * address is `stamp.address.line`, the tenant's own, labelled as such.
 */

import type { GeoPermissionReport } from '@/media/useGeoEvidence';
import { encodePlusCode } from './plusCode';
import type { GeoFix } from './shotGeo';
import type { GeoWatchError } from './useGeoWatch';

export type CopyElection = 'SHOW' | 'HIDE';
export type LiveCardMode = 'COORDINATES' | 'HIDDEN';
export type DistanceBand = 'B50' | 'B200' | 'B1000' | 'OVER' | 'UNKNOWN';

export interface StampAnchor {
  readonly lat: number;
  readonly lng: number;
  readonly source: string;
  readonly confidence: string;
}

/** `summary.stamp` after the tolerant parse — every field present, defaults applied. */
export interface StampBlock {
  readonly coordinates: CopyElection;
  readonly liveCard: LiveCardMode;
  readonly distanceBand: boolean;
  readonly plusCode: boolean;
  readonly timezone: string | null;
  readonly address: { readonly line: string } | null;
  readonly anchor: StampAnchor | null;
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/**
 * Read `summary.stamp` defensively. Absent / unusable ⇒ null (an older be — the no-anchor form).
 * `coordinates` outside SHOW|HIDE (a `BY_KIND` that should have been resolved server-side) reads as HIDE —
 * the election that prints less. `liveCard` absent (a be between v0.1.12 and the DEC-095 addendum) reads as
 * COORDINATES, the base default the addendum names.
 */
export function parseStamp(raw: unknown): StampBlock | null {
  if (!isRecord(raw)) return null;
  const coordinates: CopyElection = raw.coordinates === 'SHOW' ? 'SHOW' : 'HIDE';
  const liveCard: LiveCardMode = raw.liveCard === 'HIDDEN' ? 'HIDDEN' : 'COORDINATES';
  const tz = typeof raw.timezone === 'string' && raw.timezone.trim() !== '' ? raw.timezone.trim() : null;
  const addr = isRecord(raw.address) && typeof raw.address.line === 'string' && raw.address.line.trim() !== '' ? { line: raw.address.line.trim() } : null;
  let anchor: StampAnchor | null = null;
  if (isRecord(raw.anchor) && finite(raw.anchor.lat) && finite(raw.anchor.lng)) {
    anchor = {
      lat: raw.anchor.lat,
      lng: raw.anchor.lng,
      source: typeof raw.anchor.source === 'string' ? raw.anchor.source : 'UNKNOWN',
      confidence: typeof raw.anchor.confidence === 'string' ? raw.anchor.confidence.toUpperCase() : 'UNKNOWN',
    };
  }
  return {
    coordinates,
    liveCard,
    distanceBand: raw.distanceBand !== false,
    plusCode: raw.plusCode !== false,
    timezone: tz,
    address: addr,
    anchor,
  };
}

// ── geo, mirroring the be's GeoMath ─────────────────────────────────────────────────────────────

export const EARTH_RADIUS_M = 6371008.8;

export function haversineMetres(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const toRad = (d: number): number => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return 2 * EARTH_RADIUS_M * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Bands are scored evidence, never a boolean: ≤50 m · ≤200 m · ≤1 000 m · over (the be's `GeoMath.band`). */
export function bandOf(metres: number | null | undefined): DistanceBand {
  if (metres === null || metres === undefined || Number.isNaN(metres) || metres < 0) return 'UNKNOWN';
  if (metres <= 50) return 'B50';
  if (metres <= 200) return 'B200';
  if (metres <= 1000) return 'B1000';
  return 'OVER';
}

/** `42 m` below a kilometre, `1.2 km` from 999.5 m up — the be's `SvStampComposer.metres`. */
export function metresWords(distanceM: number): string {
  if (distanceM < 999.5) return `${Math.round(distanceM)} m`;
  return `${(distanceM / 1000).toFixed(1)} km`;
}

/** `14:40 IST` — the instant in the given IANA zone (line 2's local clock). Null when the zone is unknown to this engine. */
export function localClock(atMs: number, zone: string): string | null {
  try {
    const parts = new Intl.DateTimeFormat('en-IN', { timeZone: zone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZoneName: 'short' }).formatToParts(new Date(atMs));
    const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? '';
    const hh = get('hour').padStart(2, '0');
    const mm = get('minute').padStart(2, '0');
    const tz = get('timeZoneName');
    if (!hh || !mm) return null;
    return tz ? `${hh}:${mm} ${tz}` : `${hh}:${mm}`;
  } catch {
    return null;
  }
}

// ── the model ───────────────────────────────────────────────────────────────────────────────────

export type DistanceLine =
  | { readonly kind: 'measured'; readonly metres: number; readonly words: string; readonly band: DistanceBand }
  | { readonly kind: 'centroid' }
  | { readonly kind: 'server' };

export interface PreviewLines {
  /** `19.0762, 72.8779` (4 dp) when the live card shows coordinates; null under `liveCard = HIDDEN`. */
  readonly coordinates: string | null;
  /** The 10-character Plus code — only beside shown coordinates and when `stamp.plusCode`; else null. */
  readonly plusCode: string | null;
  /** Null when the tenant turned the band off. */
  readonly distance: DistanceLine | null;
  /** `14:40 IST` in the tenant zone; null without a zone. */
  readonly clock: string | null;
  /** The tenant's own registered address line, or null. Never a browser-derived street. */
  readonly address: string | null;
  /** True under the copy election HIDE: the coordinates are recorded but not printed on shared copies. */
  readonly copyHidden: boolean;
  /**
   * Why the live refresh is not delivering (§C.4): the fix shown is the LAST one remembered, with its true age —
   * never a stale value presented as fresh. Null while the watch is live (or while frozen to a shot).
   */
  readonly watchNote: GeoWatchError | null;
}

export type PreviewModel =
  | { readonly kind: 'waiting' }
  | { readonly kind: 'denied' }
  | { readonly kind: 'unsupported' }
  | {
      readonly kind: 'fix';
      readonly lat: number;
      readonly lng: number;
      readonly accuracyM: number;
      readonly collectedAt: string;
      /** True while a shot is in flight and this is the fix its chunks bind. */
      readonly frozen: boolean;
      readonly ageS: number;
      readonly lines: PreviewLines;
    };

/** 4 decimal places (≈ 11 m) — the live-card precision (M1). */
export function formatCoordinate(n: number): string {
  return n.toFixed(4);
}

export function distanceLineFor(fix: GeoFix, stamp: StampBlock | null): DistanceLine | null {
  if (!stamp) return { kind: 'server' };
  if (!stamp.distanceBand) return null;
  if (!stamp.anchor) return { kind: 'server' };
  if (stamp.anchor.confidence === 'LOW' || stamp.anchor.source === 'PINCODE_CENTROID') return { kind: 'centroid' };
  const metres = haversineMetres(fix.lat, fix.lng, stamp.anchor.lat, stamp.anchor.lng);
  return { kind: 'measured', metres, words: metresWords(metres), band: bandOf(metres) };
}

/**
 * The card's model. `boundFix` (a shot in flight) wins over `liveFix`: the card must not repaint mid-shot.
 * `stamp === null` (an older be) ⇒ the no-anchor form: coordinates per the base default, accuracy, the
 * "measured by the server after capture" line, nothing else. `watch` is the live refresh's last error
 * (`useGeoWatch`): with a fix held it becomes the honest note under the age; without one it decides the
 * fix-less state ahead of the visit-level permission report.
 */
export function previewModel(
  liveFix: GeoFix | null,
  permission: GeoPermissionReport,
  stamp: StampBlock | null,
  now: number,
  boundFix: GeoFix | null = null,
  watch: GeoWatchError | null = null,
): PreviewModel {
  const fix = boundFix ?? liveFix;
  if (!fix) {
    if (watch === 'denied' || permission === 'DENIED') return { kind: 'denied' };
    if (watch === 'unsupported' || permission === 'UNSUPPORTED') return { kind: 'unsupported' };
    return { kind: 'waiting' };
  }
  const liveCard = stamp?.liveCard ?? 'COORDINATES';
  const showCoords = liveCard === 'COORDINATES';
  const lines: PreviewLines = {
    coordinates: showCoords ? `${formatCoordinate(fix.lat)}, ${formatCoordinate(fix.lng)}` : null,
    plusCode: showCoords && stamp?.plusCode ? encodePlusCode(fix.lat, fix.lng) : null,
    distance: distanceLineFor(fix, stamp),
    clock: stamp?.timezone ? localClock(now, stamp.timezone) : null,
    address: stamp?.address?.line ?? null,
    copyHidden: stamp?.coordinates === 'HIDE',
    // frozen to a shot ⇒ the watch is cleared by design; nothing to say about refresh
    watchNote: boundFix ? null : watch,
  };
  return {
    kind: 'fix',
    lat: fix.lat,
    lng: fix.lng,
    accuracyM: fix.accuracyM,
    collectedAt: fix.collectedAt,
    frozen: boundFix !== null,
    ageS: Math.max(0, Math.round((now - Date.parse(fix.collectedAt)) / 1000)),
    lines,
  };
}
